import { create } from 'zustand';
import { oilOrdersApi, CartItem } from '../api/oilOrders';

interface OilCartState {
  items: CartItem[];
  loading: boolean;

  loadCart: () => Promise<void>;
  setQty: (sizeId: number, supplierId: number, origin: string | null, qty: number) => Promise<void>;
  clearCart: () => Promise<void>;
  getQty: (sizeId: number, supplierId: number, origin: string | null) => number;
  hasItems: (sizeId: number, origin: string | null) => boolean;
  hasItemsForSize: (sizeId: number) => boolean;
}

const useOilCartStore = create<OilCartState>((set, get) => ({
  items: [],
  loading: false,

  loadCart: async () => {
    set({ loading: true });
    try {
      const items = await oilOrdersApi.getCart();
      set({ items });
    } finally {
      set({ loading: false });
    }
  },

  setQty: async (sizeId: number, supplierId: number, origin: string | null, qty: number) => {
    const match = (i: CartItem) => i.size_id === sizeId && i.supplier_id === supplierId && i.origin === origin;
    const clampedQty = Math.max(0, qty);
    // Запомнить предыдущее значение только для этого item
    const prevItem = get().items.find(match);
    const prevQty = prevItem?.qty ?? 0;

    // Оптимистичное обновление
    if (clampedQty <= 0) {
      set({ items: get().items.filter(i => !match(i)) });
    } else if (get().items.some(match)) {
      set({ items: get().items.map(i => match(i) ? { ...i, qty: clampedQty } : i) });
    } else {
      set({ items: [...get().items, { size_id: sizeId, supplier_id: supplierId, origin, qty: clampedQty }] });
    }

    try {
      await oilOrdersApi.upsertCartItem({ size_id: sizeId, supplier_id: supplierId, origin, qty: clampedQty });
    } catch {
      // Откат только этого item к предыдущему значению
      if (prevQty <= 0) {
        set({ items: get().items.filter(i => !match(i)) });
      } else {
        set({ items: get().items.map(i => match(i) ? { ...i, qty: prevQty } : i) });
      }
    }
  },

  clearCart: async () => {
    const prev = get().items;
    set({ items: [] });
    try {
      await oilOrdersApi.clearCart();
    } catch {
      set({ items: prev });
    }
  },

  getQty: (sizeId: number, supplierId: number, origin: string | null) => {
    return get().items.find(i => i.size_id === sizeId && i.supplier_id === supplierId && i.origin === origin)?.qty ?? 0;
  },

  hasItems: (sizeId: number, origin: string | null) => {
    return get().items.some(i => i.size_id === sizeId && i.origin === origin && i.qty > 0);
  },

  hasItemsForSize: (sizeId: number) => {
    return get().items.some(i => i.size_id === sizeId && i.qty > 0);
  },
}));

export default useOilCartStore;
